
export type NotificationType = 
  // Chat & Communication
  | 'mention'
  | 'message'
  | 'venue_message'
  
  // Game & Sports
  | 'game_notification'
  | 'score_update'
  | 'tournament_update'
  | 'scorekeeper_opportunity'
  
  // Social & Followers
  | 'new_follower'
  | 'follow_request'
  | 'team_invite'
  
  // Account & Security
  | 'sign_in'
  | 'account_blocked'
  | 'account_disabled'
  | 'account_investigation'
  | 'password_changed'
  | 'login_attempt'
  
  // Coach Specific
  | 'challenge'
  | 'violation'
  | 'rule_update'
  | 'injury_report'
  | 'new_signup'
  | 'needs_attention'
  | 'payment_due'
  | 'parent_notification'
  | 'team_update'
  
  // Player Specific
  | 'practice_reminder'
  | 'performance_update'
  | 'achievement'
  | 'team_selection'
  
  // Parent Specific
  | 'child_activity'
  | 'permission_request'
  | 'emergency_contact'
  | 'payment_reminder'
  | 'parental_consent_required'
  
  // Venue Specific
  | 'booking_request'
  | 'facility_update'
  | 'maintenance_notice'
  
  // Season Management
  | 'season_rollover'
  
  // System & Admin
  | 'system_update'
  | 'maintenance'
  | 'policy_update'
  | 'feature_announcement'
  | 'shop_launch';

export type NotificationCategory = 
  | 'general'
  | 'chat'
  | 'game'
  | 'account'
  | 'coach'
  | 'player'
  | 'parent'
  | 'venue'
  | 'system'
  | 'security'
  | 'payment'
  | 'emergency';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface EnhancedNotification {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  notification_type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  priority: NotificationPriority;
  is_read: boolean;
  requires_action: boolean;
  action_url?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreference {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  notification_type: NotificationType;
  enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  created_at: string;
  updated_at: string;
}

// Role-based notification configuration
export const ROLE_NOTIFICATION_TYPES: Record<string, NotificationType[]> = {
  coach: [
    'challenge', 'violation', 'rule_update', 'injury_report', 
    'new_signup', 'needs_attention', 'payment_due', 'parent_notification',
    'team_update', 'game_notification', 'score_update', 'mention', 'message'
  ],
  player: [
    'practice_reminder', 'performance_update', 'achievement', 'team_selection',
    'game_notification', 'score_update', 'team_invite', 'mention', 'message'
  ],
  parent: [
    'child_activity', 'permission_request', 'emergency_contact', 'payment_reminder',
    'parent_notification', 'game_notification', 'mention', 'message', 'parental_consent_required'
  ],
  venue: [
    'booking_request', 'facility_update', 'maintenance_notice', 'venue_message',
    'payment_due', 'mention', 'message'
  ],
  'team-owner': [
    'team_update', 'payment_due', 'new_signup', 'game_notification',
    'score_update', 'challenge', 'mention', 'message'
  ],
  fan: [
    'game_notification', 'score_update', 'team_update', 'tournament_update',
    'new_follower', 'follow_request', 'mention', 'message'
  ],
  admin: [
    'system_update', 'account_blocked', 'account_investigation', 'violation',
    'needs_attention', 'maintenance', 'policy_update'
  ]
};
